///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, HostListener, Inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { ProductsRadioTabsEnum } from '../../../entities/product-radio/products-radio-tabs.enum';
import { GtthAlertService } from '../../../services/alert.service';
import { GtthEventManager } from '../../../services/event-manager.service';
import { GtthHistoryService } from '../../../project-view-panel/logical-topology/engine/history-service';
import { _cloneDeep, _isEqual } from '../../../shared/utils';
import { GtthUpdateMainConfigCommand } from '../../../project-view-panel/logical-topology/engine/commands/project/update-main-config-command';
import { GtthRefreshTopologyCommand } from '../../../project-view-panel/logical-topology/engine/commands/project/refresh-topology-command';
import { ProjectProductsService } from '../../../project-view-panel/logical-topology/engine/project-products-service';
import {
    ConfigPropertiesDTO,
    EngineProcessTypeEnum,
    EngineProductDTO,
    ExtendedProductdata,
    GtthLayerDTO,
    PlannerFeatureCollectionDTO,
    ProductTypeEnum,
    ProjectDTO,
    ProjectFolderEnum
} from '../../../generated/shared-types.model';
import { GtthAutoDesignCommand } from '../../../project-view-panel/logical-topology/engine/commands/design/auto-design-command';
import { ProjectService } from '../../../services/project/project.service';
import { GtthProjectInfoViewService } from '../../../layouts/navbar/project-info-view.service';
import { GtthProjectStateService } from '../../../project-view-panel/logical-topology/engine/project-state.service';
import { GtthLogicalTopology } from '../../../project-view-panel/logical-topology/entities/logical-topology';
import { GtthActivateLayerCommand } from '../../../project-view-panel/logical-topology/engine/commands/toolbar/activate-layer-command';
import { GtthLayerDtoModel } from '../../../services/project-socket/dto/gtth-layer-dto.model';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ProjectDialogGeneralTabComponent } from './general-tab/project-dialog-general-tab.component';
import { ProjectDialogProductConfigTabComponent } from './product-config-tab/project-dialog-product-config-tab.component';
import { ProjectDialogProductsService } from './project-dialog-products.service';
import { ProjectDialogNwConfigTabComponent } from './nw-config-tab/project-dialog-nw-config-tab.component';
import { ExportLinkConfig } from '../../../services/logical-topology/export-link-config.model';
import { AutoDesignPtMpModel } from '../../../services/logical-topology/design/auto-design-ptmp.model';
import { LogicalConfigModel } from '../../../services/logical-topology/logical-config.model';
import { createProject } from '../store/project-modal-management/project-modal-management.actions';
import { Store } from '@ngrx/store';
import { LoaderIds, PreloaderService } from '../../../services/preloader.service';
import { finalize } from 'rxjs';
import { isDefined } from '@siklu/sui-lib/utils';

@Component({
    selector: 'winde-project-gtth-dialog',
    templateUrl: './project-dialog.component.html',
    styleUrls: ['../../projects.scss'],
    providers: [ProjectDialogProductsService],
    standalone: false
})
export class ProjectDialogComponent implements OnInit {
    public static readonly DEFAULT_LAYER_NAMES = ['Access', 'Distribution', 'Backhaul'];
    public static readonly LOCATION_DEFAULT_PREFIX = 'loc';
    public static readonly DEFAULT_HEIGHT = 7;

    @ViewChild(ProjectDialogGeneralTabComponent) generalTabComponent: ProjectDialogGeneralTabComponent;
    @ViewChild(ProjectDialogProductConfigTabComponent) productsTabComponent: ProjectDialogProductConfigTabComponent;
    @ViewChild(ProjectDialogNwConfigTabComponent) nwTabComponent: ProjectDialogNwConfigTabComponent;

    public isLoading = false;

    public readonly tabsToShow = [
        ProductsRadioTabsEnum[ProductsRadioTabsEnum.GENERAL],
        ProductsRadioTabsEnum[ProductsRadioTabsEnum.PRODUCT_CONFIG],
        ProductsRadioTabsEnum[ProductsRadioTabsEnum.NW_CONFIG]
    ];
    public productsRadioTabs = ProductsRadioTabsEnum;
    public currentTab = 0;

    public shouldOpenProject = true;

    public project: ProjectDTO;
    public logicalConfig: LogicalConfigModel;

    public isSaving: boolean;
    public viewInReadOnly = false;
    public potentiallyCorruptedProject = false;

    public loaderKey = LoaderIds.PROJECT_DIALOG_PRELOADER_ID;

    private newProjectId: number;

    private originProject: ProjectDTO;
    private originLogicalConfigProps: ConfigPropertiesDTO;

    constructor(
        private readonly store: Store,
        private readonly alertService: GtthAlertService,
        private readonly projectService: ProjectService,
        private readonly eventManager: GtthEventManager,
        private readonly projectInfoService: GtthProjectInfoViewService,
        private readonly router: Router,
        private readonly projectProductsService: ProjectProductsService,
        private readonly projectStateService: GtthProjectStateService,
        private readonly dialogProductsService: ProjectDialogProductsService,
        public readonly historyService: GtthHistoryService,
        public readonly preloaderService: PreloaderService,
        public dialogRef: MatDialogRef<ProjectDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { project: ProjectDTO }
    ) {}

    @HostListener('document:keydown', ['$event'])
    handleKeyboardUpEvent(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            this.save();
        }
    }

    ngOnInit() {
        this.preloaderService.show(this.loaderKey);
        this.isLoading = true;
        this.isSaving = false;
        this.project = this.data.project;

        if (isDefined(this.project.id)) {
            this.originProject = _cloneDeep(this.project);

            if (this.project.engineType === EngineProcessTypeEnum.RINGDES_V_1211) {
                this.potentiallyCorruptedProject = true;
            } else {
                this.projectService.canBeModified(this.project.id).subscribe((projectIsNotLocked: boolean) => {
                    this.viewInReadOnly = !projectIsNotLocked;
                });
            }

            this.projectService.temp_getProjectLogicConfig(this.project.id).subscribe((geoJson: PlannerFeatureCollectionDTO) => {
                this.logicalConfig = geoJson as LogicalConfigModel;
                this.originLogicalConfigProps = _cloneDeep(this.logicalConfig.crs.properties);
                this.createLayers();

                this.dialogProductsService
                    .loadRadioAndAntennaAndMhProducts(this.project, this.logicalConfig)
                    .pipe(finalize(() => this.preloaderService.hide(this.loaderKey)))
                    .subscribe(() => {
                        this.isLoading = false;
                    });
            });
        } else {
            this.originProject = _cloneDeep(this.project);
            this.logicalConfig = LogicalConfigModel.createDefault(false);

            this.createLayers();

            this.originLogicalConfigProps = _cloneDeep(this.logicalConfig.crs.properties);
            this.dialogProductsService
                .loadRadioAndAntennaAndMhProducts(this.project, this.logicalConfig)
                .pipe(finalize(() => this.preloaderService.hide(this.loaderKey)))
                .subscribe(() => {
                    this.isLoading = false;
                });
        }
    }

    createLayers() {
        if (!this.logicalConfig.crs.properties.layers?.length) {
            this.logicalConfig.crs.properties.layers = [];
            for (let i = 0; i < ProjectDialogComponent.DEFAULT_LAYER_NAMES.length; i++) {
                if (i > 0) {
                    this.logicalConfig.crs.properties.tuRequiredRatePerLayer[i] =
                        this.logicalConfig.crs.properties.tuRequiredRatePerLayer[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX];
                    this.logicalConfig.crs.properties.defaultHeightPerLayer[i] =
                        this.logicalConfig.crs.properties.defaultHeightPerLayer[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX];
                    this.logicalConfig.crs.properties.selectedProductsPerLayer[i] = _cloneDeep(
                        this.logicalConfig.crs.properties.selectedProductsPerLayer[i]
                    );

                    this.logicalConfig.crs.properties.designRingsPerLayer[i] = GtthAutoDesignCommand.createDesignRindsDTO();
                    this.logicalConfig.crs.properties.autodesignP2mpPerLayer[i] = AutoDesignPtMpModel.createAutoDesignPtMpModel(
                        undefined,
                        false
                    );
                    this.logicalConfig.crs.properties.autoSetFrequenciesPerLayer[i] = GtthAutoDesignCommand.createAutoSetFrequenciesDTO();
                    this.logicalConfig.crs.properties.isBlockedByPole[i] = false;
                }
                this.logicalConfig.crs.properties.layers[i] = new GtthLayerDtoModel(i, ProjectDialogComponent.DEFAULT_LAYER_NAMES[i]);
            }
        }
        if (!isDefined(this.logicalConfig.crs.properties.isBlockedByPole)) {
            this.logicalConfig.crs.properties.isBlockedByPole = [];
            this.logicalConfig.crs.properties.layers.forEach(() => this.logicalConfig.crs.properties.isBlockedByPole.push(false));
        }
        this.logicalConfig.crs.properties.autodesignP2mpPerLayer.forEach((autodesignP2mpPerLayer: AutoDesignPtMpModel) => {
            if (!isDefined(autodesignP2mpPerLayer.pole_block)) {
                autodesignP2mpPerLayer.pole_block = 40;
            }
        });
    }

    changeTab(tabIndex: string) {
        const tabIsValid = this.applyTabsData();

        if (tabIsValid) {
            this.currentTab = ProductsRadioTabsEnum[tabIndex];
        }
    }

    save() {
        const currentTabIsActive = this.applyTabsData();

        if (this.isSaving || !currentTabIsActive) {
            return;
        }

        if (
            _isEqual(this.project, this.originProject) &&
            _isEqual(this.logicalConfig.crs.properties, this.originLogicalConfigProps) &&
            _isEqual(this.logicalConfig.crs.properties.selectedProductsPerLayer, this.originLogicalConfigProps.selectedProductsPerLayer) &&
            this.dialogProductsService.getChangedPdbList().length === 0
        ) {
            return this.dialogRef.close();
        }

        this.preloaderService.show(this.loaderKey);
        this.isSaving = true;

        if (!this.logicalConfig.crs.properties.layersModeEnabled) {
            this.logicalConfig.crs.properties.activeLayerIdx = 0;
        }

        if (this.projectStateService.projectIsOpened() && this.projectStateService.getProjectId() === this.project.id) {
            this.historyService.logicalTopology.basic_discount = this.logicalConfig.crs.properties.basic_discount;
            this.historyService.logicalTopology.locationNamePrefix = this.logicalConfig.crs.properties.locationNamePrefix;
            this.historyService.logicalTopology.productUnits = this.logicalConfig.crs.properties.productUnits;
            this.historyService.logicalTopology.defaultHeightPerLayer = this.logicalConfig.crs.properties.defaultHeightPerLayer;
            this.historyService.logicalTopology.isBlockedByPole = this.logicalConfig.crs.properties.isBlockedByPole;
            this.historyService.logicalTopology.selectedProducts = this.logicalConfig.crs.properties.selectedProductsPerLayer;
            this.historyService.logicalTopology.defaultBuProductPerLayer = this.logicalConfig.crs.properties.defaultBuProductPerLayer;
            this.historyService.logicalTopology.defaultTuProductPerLayer = this.logicalConfig.crs.properties.defaultTuProductPerLayer;

            if (!this.logicalConfig.crs.properties.layersModeEnabled) {
                this.historyService.logicalTopology.activeLayerIdx = this.logicalConfig.crs.properties.activeLayerIdx;
            }
            if (this.historyService.logicalTopology.layersModeEnabled !== this.logicalConfig.crs.properties.layersModeEnabled) {
                this.historyService.clearHistory();
                const activeLayerCommand = this.historyService
                    .command(GtthActivateLayerCommand)
                    .create(this.historyService.logicalTopology.activeLayerIdx);
                this.historyService.doCommandBypassHistory(activeLayerCommand);
            }

            this.historyService.logicalTopology.layersModeEnabled = this.logicalConfig.crs.properties.layersModeEnabled;
            if (this.logicalConfig.crs.properties.layers?.length) {
                this.logicalConfig.crs.properties.layers.forEach((layer: GtthLayerDTO, i: number) => {
                    this.historyService.logicalTopology.layers[i] = new GtthLayerDtoModel(i, layer.layerName);
                });
            }
            this.historyService.projectInfoViewService.showInfo();
            if (this.historyService.logicalTopology.layersModeEnabled) {
                this.historyService.projectInfoViewService.showLayers(
                    this.historyService.logicalTopology.layers,
                    this.historyService.logicalTopology.activeLayerIdx
                );
            } else {
                this.historyService.projectInfoViewService.hideLayers();
            }
            this.historyService.logicalTopology.designRingsPerLayer = this.logicalConfig.crs.properties.designRingsPerLayer;
            this.historyService.logicalTopology.autoSetFrequenciesPerLayer = this.logicalConfig.crs.properties.autoSetFrequenciesPerLayer;
            this.historyService.logicalTopology.autodesignP2mpPerLayer = this.logicalConfig.crs.properties.autodesignP2mpPerLayer;
            this.historyService.logicalTopology.exportLinksConfig = new ExportLinkConfig(
                this.logicalConfig.crs.properties.saved_exportLinksConfig
            );
        }
        this.updateResiliencyParam(!isDefined(this.project.id));

        const handleResponse = (res: ProjectDTO) => {
            if (this.project.id !== undefined) {
                if (res) {
                    this.updateProjectConfig(res);
                } else {
                    this.isSaving = false;
                }
            } else if (res) {
                this.newProjectId = res.id;
                this.updateProjectConfig(res);
                this.store.dispatch(createProject());
            } else {
                this.isSaving = false;
            }
        };

        if (this.project.id !== undefined) {
            this.projectService
                .update(this.project)
                .pipe(finalize(() => this.preloaderService.hide(this.loaderKey)))
                .subscribe({
                    next: (res: ProjectDTO) => handleResponse(res),
                    error: (res: Response) => this.onSaveError(res)
                });
        } else {
            this.projectService
                .create(this.project, 0, 0, 1)
                .pipe(finalize(() => this.preloaderService.hide(this.loaderKey)))
                .subscribe({
                    next: (res: ProjectDTO) => handleResponse(res),
                    error: (res: Response) => this.onSaveError(res)
                });
        }
    }

    updateResiliencyParam(isProjectCreate: boolean): void {
        this.logicalConfig.crs.properties.layers.forEach((value: GtthLayerDTO, layerIndex: number) => {
            const layerHasMhTg = this.logicalConfig.crs.properties.selectedProductsPerLayer[layerIndex].some(
                (productData: ExtendedProductdata) =>
                    this.dialogProductsService.getEngineProduct(productData.usedProd)?.productType === ProductTypeEnum.TG_MH
            );
            if (!isProjectCreate && !layerHasMhTg) {
                // disable resiliency by default and if project products contain no MH-TG
                this.logicalConfig.crs.properties.autodesignP2mpPerLayer[layerIndex].redun_fac =
                    AutoDesignPtMpModel.REDUND_FAC_FOR_NO_RESILIENCY;
                this.logicalConfig.crs.properties.autodesignP2mpPerLayer[layerIndex].tunnel_fac =
                    AutoDesignPtMpModel.TUNNEL_FAC_DEFAULT_VALUE_FOR_NO_RESILIENCY;
            }
        });
    }

    private updateProjectConfig(newProject: ProjectDTO) {
        if (this.projectStateService.projectIsOpened() && this.projectStateService.getProjectId() === newProject.id) {
            const updateMainConfigCommand = this.historyService.command(GtthUpdateMainConfigCommand);
            this.historyService.doCommandBypassHistory(updateMainConfigCommand).then(() => {
                if (
                    !_isEqual(
                        this.logicalConfig.crs.properties.selectedProductsPerLayer,
                        this.originLogicalConfigProps.selectedProductsPerLayer
                    )
                ) {
                    this.projectProductsService.loadProducts(newProject.id, true).then(() => this.updateProjectPdbConfig(newProject.id));
                } else {
                    this.updateProjectPdbConfig(newProject.id);
                }
            });
            this.projectInfoService.setProjectName(newProject.name);
        } else {
            this.projectService
                .temp_updateProjectMainConfig(newProject.id, this.logicalConfig)
                .pipe(finalize(() => this.preloaderService.hide(this.loaderKey)))
                .subscribe({
                    next: (res: PlannerFeatureCollectionDTO) => {
                        if (res) {
                            this.updateProjectPdbConfig(newProject.id);
                        } else {
                            this.isSaving = false;
                        }
                    },
                    error: (res: Response) => this.onSaveError(res)
                });
        }
    }

    private updateProjectPdbConfig(projectId: number): void {
        const changedEngineRadioList: EngineProductDTO[] = this.dialogProductsService.getChangedPdbList();

        if (changedEngineRadioList.length > 0) {
            this.projectService
                .updateProjectPdbConfig(projectId, changedEngineRadioList)
                .pipe(finalize(() => this.preloaderService.hide(this.loaderKey)))
                .subscribe((result: boolean) => {
                    if (result) {
                        if (this.projectStateService.projectIsOpened() && this.projectStateService.getProjectId() === projectId) {
                            const refreshTopologyConfigCommand = this.historyService
                                .command(GtthRefreshTopologyCommand)
                                .create(false, false);
                            this.historyService.doCommandBypassHistory(refreshTopologyConfigCommand).then(() => {
                                this.onSaveSuccess();
                            });
                        } else {
                            this.onSaveSuccess();
                        }
                    } else {
                        this.isSaving = false;
                    }
                });
        } else {
            this.onSaveSuccess();
        }
    }

    private onSaveSuccess() {
        this.eventManager.broadcast({ name: 'projectModified', content: 'OK' });
        this.preloaderService.hide(this.loaderKey);

        this.isSaving = false;
        this.dialogRef.close();

        if (isDefined(this.newProjectId) && this.shouldOpenProject) {
            setTimeout(() => {
                this.router.navigate(['/project-view', this.newProjectId]);
            }, 0);
        }
    }

    private onSaveError(error) {
        try {
            error.json();
        } catch (exception) {
            error.message = error.text();
            console.log(`Failed to save - ${exception}`);
        }
        this.isSaving = false;
        this.alertService.error(error.message, null, null);
    }

    private applyTabsData(): boolean {
        switch (this.currentTab) {
            case ProductsRadioTabsEnum.GENERAL:
                return this.generalTabComponent.validateAndApply();
            case ProductsRadioTabsEnum.PRODUCT_CONFIG:
                return this.productsTabComponent.validateAndApply();
            case ProductsRadioTabsEnum.NW_CONFIG:
                return this.nwTabComponent.validateAndApply();
        }
    }
}

@Component({
    selector: 'winde-project-gtth-popup',
    template: '',
    standalone: false
})
export class ProjectPopupComponent implements OnInit {
    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly dialog: MatDialog,
        private readonly projectService: ProjectService
    ) {}

    ngOnInit() {
        const params = this.route.snapshot.paramMap;
        const id = params.has('id') ? parseInt(params.get('id'), 10) : undefined;

        if (isDefined(id)) {
            this.projectService.getProject(id).subscribe((project: ProjectDTO) => {
                if (!project) {
                    this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
                } else {
                    const dialogRef = this.dialog.open(ProjectDialogComponent, {
                        width: '1300px',
                        position: {
                            top: '30px'
                        },
                        data: {
                            project
                        },
                        closeOnNavigation: true
                    });

                    dialogRef.afterClosed().subscribe(() => {
                        this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
                    });
                }
            });
        } else {
            const rootFolderIds = this.route.snapshot.data.rootFolderIds;
            const customerId = params.has('customerId') ? parseInt(params.get('customerId'), 10) : undefined;
            const parentId = params.has('parentId') ? parseInt(params.get('parentId'), 10) : rootFolderIds[ProjectFolderEnum.PROJECT];

            const project = {} as ProjectDTO;
            project.customerId = customerId;
            project.parentId = parentId;
            project.engineType = EngineProcessTypeEnum.RINGDES_LATEST;

            const dialogRef = this.dialog.open(ProjectDialogComponent, {
                width: '1300px',
                position: {
                    top: '30px'
                },
                data: {
                    project
                },
                closeOnNavigation: true
            });

            dialogRef.afterClosed().subscribe(() => {
                this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
            });
        }
    }
}
